'use client'

import * as React from 'react'
import Image from 'next/image'
import { Typography } from '@repo/web-ui/components/typography'
import { ChevronLeftIcon, ChevronRightIcon } from 'primitives/icons'
import { Link } from '@repo/web-ui/components/link'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import { capitaliseFirstLetter } from 'utils/string'
import debounce from 'lodash.debounce'

import { Card } from './card'
import type {
  ProductCardProduct,
  ProductCardProductEnrolled,
} from '../_hooks/product-card-product-type'
import { HoverActions } from '@web/components/hover-actions/hover-actions'
import { ProductBoxCheck } from './product-box-check'
import { SchoolIconFill } from './icons'
import { LevelBadge } from './product-box/level-badge'
import { convertHighCount } from '@web/utils/counter'
import { SourceBadge } from './product-box/source-badge'
import { Typography as DSTypography } from '@repo/design-system-cf/components/typography'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { Button } from '@repo/design-system-cf/components/button'
import { DownloadIcon } from '@web/app/[locale]/(web)/masterfeed/_components/assets/icons'
import { useTrendingNowProductsQuery } from '../_hooks/use-trending-now-products-query'

const SCROLL_BUTTON_FADE_EDGE_OFFSET = 20

export function ProductBoxSlider({
  title,
  description,
  products,
}: {
  products: ProductCardProduct[]
  title: string
  description?: string
}) {
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
  const scrollToItemsRef = React.useRef<(HTMLDivElement | null)[]>([])

  const [isLeftButtonVisible, setIsLeftButtonVisible] = React.useState(false)
  const [isRightButtonVisible, setIsRightButtonVisible] = React.useState(false)

  const findNextItem = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current
    const items = scrollToItemsRef.current
    const containerRect = container?.getBoundingClientRect()
    let nextItem = items[direction === 'left' ? 0 : items.length - 1]
    if (!containerRect) {
      return nextItem
    }
    // oxlint-disable-next-line typescript/prefer-for-of
    for (let i = 0; i < items.length; i++) {
      const itemRect = items[i]?.getBoundingClientRect()
      if (direction === 'right' && Number(itemRect?.left) > Number(containerRect?.right)) {
        nextItem = items[i]
        break
      }
      if (direction === 'left' && Number(itemRect?.right) < Number(containerRect?.left)) {
        nextItem = items[i]
        // continuue the loop to find the leftmost item that is not in the view.
        continue
      }
    }
    return nextItem
  }

  const handleScrollRight = () => {
    const nextItem = findNextItem('right')
    if (nextItem) {
      nextItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  const handleScrollLeft = () => {
    const nextItem = findNextItem('left')
    if (nextItem) {
      nextItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  const handleScroll = React.useMemo(
    () =>
      debounce(
        () => {
          const container = scrollContainerRef.current
          if (!container) {
            return
          }
          const containerRect = container.getBoundingClientRect()
          const scrollLeft = container.scrollLeft
          const scrollWidth = container.scrollWidth
          const containerWidth = containerRect.width
          // Show and hide the scroll buttons based on the scroll position, hide at the edges
          setIsLeftButtonVisible(scrollLeft > SCROLL_BUTTON_FADE_EDGE_OFFSET)
          setIsRightButtonVisible(
            scrollLeft + containerWidth < scrollWidth - SCROLL_BUTTON_FADE_EDGE_OFFSET,
          )
        },
        150,
        { trailing: true }, // Ensure the last call is executed
      ),
    [],
  )

  React.useEffect(() => {
    // Initialize the scroll buttons visibility
    handleScroll()
  }, [handleScroll])

  return (
    <Card className="px-0 pb-0">
      <div className="flex flex-col">
        <div className="px-6">
          <Typography variant="heading-3" className="capitalize text-gray-100">
            {title}
          </Typography>
          {description ? <Typography variant="body">{description}</Typography> : null}
        </div>
        <div className="relative px-4">
          <div
            className="@md:snap-none flex snap-x gap-1 overflow-x-auto py-6"
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            {products.map((product, index) => (
              <div
                key={product.id}
                id={product.id}
                className="shrink-0 snap-start"
                ref={el => {
                  scrollToItemsRef.current[index] = el
                }}
              >
                <ProductBox product={product} />
                <ScrollToHelper id={product.id} />
              </div>
            ))}
          </div>
          <button
            aria-hidden={isLeftButtonVisible ? 'false' : 'true'}
            tabIndex={
              isLeftButtonVisible ? 0 : -1 /* Ensure the button is not focusable when hidden */
            }
            className={tw(
              'bg-cf-white @md:flex absolute left-0 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded border border-gray-300 transition-opacity',
              isLeftButtonVisible
                ? '@md:pointer-events-auto pointer-events-none opacity-100'
                : 'pointer-events-none opacity-0',
            )}
            onClick={handleScrollLeft}
          >
            <ChevronLeftIcon className="h-12 w-12 text-gray-300" />
          </button>
          <button
            aria-hidden={isRightButtonVisible ? 'false' : 'true'}
            tabIndex={
              isRightButtonVisible ? 0 : -1 /* Ensure the button is not focusable when hidden */
            }
            className={tw(
              'bg-cf-white @md:flex absolute right-0 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded border border-gray-300 transition-opacity',
              isRightButtonVisible
                ? '@md:pointer-events-auto pointer-events-none opacity-100'
                : 'pointer-events-none opacity-0',
            )}
            onClick={handleScrollRight}
          >
            <ChevronRightIcon className="h-12 w-12 text-gray-300" />
          </button>
        </div>
      </div>
    </Card>
  )
}

export function ProductBoxSliderFixedButtonsGallery({
  subtitle,
  withDownloadCTA,
  offset: initialOffset,
  rows = 1,
}: {
  subtitle?: string
  withDownloadCTA?: boolean
  offset?: number
  rows?: 1 | 2
}) {
  const [isLoadingProducts, setIsLoadingProducts] = React.useState(false)
  const [offset, setOffset] = React.useState(initialOffset || 0)
  const [products, setProducts] = React.useState<ProductCardProduct[]>([])
  const { isLoading, data } = useTrendingNowProductsQuery(12, offset)

  const handleReachEnd = React.useCallback(() => {
    if (isLoadingProducts || isLoading) {
      return
    } // Prevent multiple calls while loading
    setIsLoadingProducts(true)
    setOffset(products?.length || 0)
  }, [isLoadingProducts, isLoading, products])

  React.useEffect(() => {
    if (isLoading || !data) {
      return
    }
    setProducts(prev => {
      const seen = new Set<string>()
      const merged = [...prev, ...data.products].filter(product => {
        if (seen.has(product.id)) {
          return false
        }
        seen.add(product.id)
        return true
      })
      return merged
    })

    setIsLoadingProducts(false)
  }, [data, isLoading])

  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [hasCalled, setHasCalled] = React.useState(false)

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
  const scrollToItemsRef = React.useRef<(HTMLDivElement | null)[]>([])

  const [isLeftButtonVisible, setIsLeftButtonVisible] = React.useState(false)
  const [isRightButtonVisible, setIsRightButtonVisible] = React.useState(false)

  const productColumns = React.useMemo(() => {
    if (rows !== 2) {
      return []
    }
    const cols: ProductCardProduct[][] = []
    for (let i = 0; i < products.length; i += 2) {
      cols.push(products.slice(i, i + 2))
    }
    return cols
  }, [products, rows])

  const getCurrentIndex = () => {
    const container = scrollContainerRef.current
    const items = scrollToItemsRef.current

    if (!container || !items.length) {
      return 0
    }

    const scrollLeft = container.scrollLeft
    let index = items.length - 1

    for (let i = 0; i < items.length; i++) {
      const el = items[i]
      if (!el) {
        continue
      }
      if (el.offsetLeft >= scrollLeft - 1) {
        index = i
        break
      }
    }

    return index
  }

  const scrollToIndex = (index: number) => {
    const container = scrollContainerRef.current
    const items = scrollToItemsRef.current

    if (!container || !items.length) {
      return
    }

    const clampedIndex = Math.max(0, Math.min(index, items.length - 1))
    const el = items[clampedIndex]

    if (!el) {
      return
    }

    if (clampedIndex === items.length - 1) {
      const targetLeft = container.scrollWidth - container.clientWidth
      container.scrollTo({ left: targetLeft, behavior: 'smooth' })
      return
    }

    const targetLeft = Math.max(
      0,
      Math.min(el.offsetLeft, container.scrollWidth - container.clientWidth),
    )

    container.scrollTo({ left: targetLeft, behavior: 'smooth' })
  }

  const handleScrollRight = () => {
    const current = getCurrentIndex()
    scrollToIndex(current + 1)
  }

  const handleScrollLeft = () => {
    const current = getCurrentIndex()
    scrollToIndex(current - 1)
  }

  React.useEffect(() => {
    if (!sentinelRef.current || !handleReachEnd) {
      return
    }
    const node = sentinelRef.current
    const rootEl = scrollContainerRef.current

    if (!rootEl) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (entry.isIntersecting && !hasCalled) {
          setHasCalled(true)
          handleReachEnd()
        } else if (!entry.isIntersecting && hasCalled) {
          // Reset so it can be called again if needed (optional)
          setHasCalled(false)
        }
      },
      {
        root: rootEl,
        rootMargin: '0px',
        threshold: 0.1,
      },
    )

    observer.observe(node)

    return () => {
      observer.unobserve(node)
      observer.disconnect()
    }
  }, [handleReachEnd, hasCalled])

  const handleScroll = React.useMemo(
    () =>
      debounce(
        () => {
          const container = scrollContainerRef.current
          if (!container) {
            return
          }
          const containerRect = container.getBoundingClientRect()
          const scrollLeft = container.scrollLeft
          const scrollWidth = container.scrollWidth
          const containerWidth = containerRect.width
          // Show and hide the scroll buttons based on the scroll position, hide at the edges
          setIsLeftButtonVisible(scrollLeft > SCROLL_BUTTON_FADE_EDGE_OFFSET)
          setIsRightButtonVisible(
            scrollLeft + containerWidth < scrollWidth - SCROLL_BUTTON_FADE_EDGE_OFFSET,
          )
        },
        16,
        { trailing: true }, // Ensure the last call is executed
      ),
    [],
  )

  React.useEffect(() => {
    // Ensure buttons state on products change
    handleScroll()
  }, [handleScroll, products.length])

  return (
    <div className="flex flex-col @container">
      <div className="relative">
        <div
          className="@md:snap-none flex snap-x gap-1 overflow-x-auto py-6"
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {rows === 2
            ? productColumns.map((col: ProductCardProduct[], colIndex: number) => (
                <div
                  key={col.map((p: ProductCardProduct) => p.id).join('-')}
                  ref={el => {
                    scrollToItemsRef.current[colIndex] = el
                  }}
                  className="shrink-0 snap-start"
                >
                  <div className="flex flex-col">
                    {col.map((product: ProductCardProduct) => (
                      <div key={product.id}>
                        <ProductBox product={product} withDownloadCTA={withDownloadCTA} />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            : products.map((product, index) => (
                <div
                  key={product.id}
                  ref={el => {
                    scrollToItemsRef.current[index] = el
                  }}
                  className="shrink-0 snap-start"
                >
                  <ProductBox product={product} withDownloadCTA={withDownloadCTA} />
                </div>
              ))}

          {isLoading ? (
            <>
              <div className="flex items-center justify-center p-6 px-20">
                <Spinner className="h-8 w-8 text-gray-300 animate-spin" />
              </div>
            </>
          ) : null}
          <div ref={sentinelRef} className="shrink-0 w-1" aria-hidden="true" />
        </div>
        <div
          className={tw('absolute -top-6 right-0 @lg:flex items-center gap-2 hidden', {
            '-top-11': subtitle,
          })}
        >
          <Button
            asChild
            variant={isLeftButtonVisible ? 'secondary' : 'tertiary'}
            disabled={!isLeftButtonVisible}
            className="h-7 w-7 flex items-center justify-center [&_*]:m-0 min-h-0 [&_svg]:h-5 [&_svg]:w-5"
          >
            <button onClick={handleScrollLeft}>
              <ChevronLeftIcon className="text-gray-300" />
            </button>
          </Button>
          <Button
            asChild
            variant={isRightButtonVisible ? 'secondary' : 'tertiary'}
            disabled={!isRightButtonVisible}
            className="h-7 w-7 plex items-center justify-center [&_*]:m-0 min-h-0 [&_svg]:h-5 [&_svg]:w-5"
          >
            <button onClick={handleScrollRight}>
              <ChevronRightIcon className="text-gray-300" />
            </button>
          </Button>
        </div>
      </div>
    </div>
  )
}

export function ProductBoxSliderFixedButtonsGalleryProducts({
  subtitle,
  products,
  withDownloadCTA,
  isLoading,
  onReachEnd,
  rows = 1,
}: {
  subtitle?: string
  withDownloadCTA?: boolean
  products: ProductCardProduct[]
  isLoading?: boolean
  onReachEnd?: () => void
  rows?: 1 | 2
}) {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [hasCalled, setHasCalled] = React.useState(false)

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
  const scrollToItemsRef = React.useRef<(HTMLDivElement | null)[]>([])

  const [isLeftButtonVisible, setIsLeftButtonVisible] = React.useState(false)
  const [isRightButtonVisible, setIsRightButtonVisible] = React.useState(false)

  const productColumns: ProductCardProduct[][] = React.useMemo(() => {
    if (rows !== 2) {
      return []
    }
    const cols: ProductCardProduct[][] = []
    for (let i = 0; i < products.length; i += 2) {
      cols.push(products.slice(i, i + 2))
    }
    return cols
  }, [products, rows])

  const getCurrentIndex = () => {
    const container = scrollContainerRef.current
    const items = scrollToItemsRef.current

    if (!container || !items.length) {
      return 0
    }

    const scrollLeft = container.scrollLeft
    let index = items.length - 1

    for (let i = 0; i < items.length; i++) {
      const el = items[i]
      if (!el) {
        continue
      }
      if (el.offsetLeft >= scrollLeft - 1) {
        index = i
        break
      }
    }

    return index
  }

  const scrollToIndex = (index: number) => {
    const container = scrollContainerRef.current
    const items = scrollToItemsRef.current

    if (!container || !items.length) {
      return
    }

    const clampedIndex = Math.max(0, Math.min(index, items.length - 1))
    const el = items[clampedIndex]

    if (!el) {
      return
    }

    if (clampedIndex === items.length - 1) {
      const targetLeft = container.scrollWidth - container.clientWidth
      container.scrollTo({ left: targetLeft, behavior: 'smooth' })
      return
    }

    const targetLeft = Math.max(
      0,
      Math.min(el.offsetLeft, container.scrollWidth - container.clientWidth),
    )

    container.scrollTo({ left: targetLeft, behavior: 'smooth' })
  }

  const handleScrollRight = () => {
    const current = getCurrentIndex()
    scrollToIndex(current + 1)
  }

  const handleScrollLeft = () => {
    const current = getCurrentIndex()
    scrollToIndex(current - 1)
  }

  React.useEffect(() => {
    if (!sentinelRef.current || !onReachEnd) {
      return
    }
    const node = sentinelRef.current
    const rootEl = scrollContainerRef.current

    if (!rootEl) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (entry.isIntersecting && !hasCalled) {
          setHasCalled(true)
          onReachEnd()
        } else if (!entry.isIntersecting && hasCalled) {
          // Reset so it can be called again if needed (optional)
          setHasCalled(false)
        }
      },
      {
        root: rootEl,
        rootMargin: '0px',
        threshold: 0.1,
      },
    )

    observer.observe(node)

    return () => {
      observer.unobserve(node)
      observer.disconnect()
    }
  }, [onReachEnd, hasCalled])

  const handleScroll = React.useMemo(
    () =>
      debounce(
        () => {
          const container = scrollContainerRef.current
          if (!container) {
            return
          }
          const containerRect = container.getBoundingClientRect()
          const scrollLeft = container.scrollLeft
          const scrollWidth = container.scrollWidth
          const containerWidth = containerRect.width
          // Show and hide the scroll buttons based on the scroll position, hide at the edges
          setIsLeftButtonVisible(scrollLeft > SCROLL_BUTTON_FADE_EDGE_OFFSET)
          setIsRightButtonVisible(
            scrollLeft + containerWidth < scrollWidth - SCROLL_BUTTON_FADE_EDGE_OFFSET,
          )
        },
        16,
        { trailing: true }, // Ensure the last call is executed
      ),
    [],
  )

  React.useEffect(() => {
    // Ensure buttons state on products change
    handleScroll()
  }, [handleScroll, products.length])

  return (
    <div className="flex flex-col @container">
      <div className="relative">
        <div
          className="@md:snap-none flex snap-x gap-1 overflow-x-auto py-6"
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {rows === 2
            ? productColumns.map((col: ProductCardProduct[], colIndex: number) => (
                <div
                  key={col.map((p: ProductCardProduct) => p.id).join('-')}
                  ref={el => {
                    scrollToItemsRef.current[colIndex] = el
                  }}
                  className="shrink-0 snap-start"
                >
                  <div className="flex flex-col">
                    {col.map((product: ProductCardProduct) => (
                      <div key={product.id}>
                        <ProductBox product={product} withDownloadCTA={withDownloadCTA} />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            : products.map((product, index) => (
                <div
                  key={product.id}
                  ref={el => {
                    scrollToItemsRef.current[index] = el
                  }}
                  className="shrink-0 snap-start"
                >
                  <ProductBox product={product} withDownloadCTA={withDownloadCTA} />
                </div>
              ))}

          {isLoading ? (
            <>
              <div className="flex items-center justify-center p-6 px-20">
                <Spinner className="h-8 w-8 text-gray-300 animate-spin" />
              </div>
            </>
          ) : null}
          <div ref={sentinelRef} className="shrink-0 w-1" aria-hidden="true" />
        </div>
        <div
          className={tw('absolute -top-6 right-0 @lg:flex items-center gap-2 hidden', {
            '-top-11': subtitle,
          })}
        >
          <Button
            asChild
            variant={isLeftButtonVisible ? 'secondary' : 'tertiary'}
            disabled={!isLeftButtonVisible}
            className="h-7 w-7 flex items-center justify-center [&_*]:m-0 min-h-0 [&_svg]:h-5 [&_svg]:w-5"
          >
            <button onClick={handleScrollLeft}>
              <ChevronLeftIcon className="text-gray-300" />
            </button>
          </Button>
          <Button
            asChild
            variant={isRightButtonVisible ? 'secondary' : 'tertiary'}
            disabled={!isRightButtonVisible}
            className="h-7 w-7 plex items-center justify-center [&_*]:m-0 min-h-0 [&_svg]:h-5 [&_svg]:w-5"
          >
            <button onClick={handleScrollRight}>
              <ChevronRightIcon className="text-gray-300" />
            </button>
          </Button>
        </div>
      </div>
    </div>
  )
}

export function ProductBoxSliderFixedButtons({
  title,
  subtitle,
  products,
  withDownloadCTA,
  isLoading,
  onReachEnd,
}: {
  title: string
  subtitle?: string
  withDownloadCTA?: boolean
  products: ProductCardProduct[]
  onReachEnd?: () => void
  isLoading?: boolean
}) {
  const rowsToUse: 1 | 2 = products.length > 6 ? 2 : 1

  return (
    <div className="@lg:px-4 flex flex-col gap-4">
      <DSTypography variant="cf-title-compact-02" className="capitalize text-gray-100">
        {title}
      </DSTypography>
      {subtitle ? <DSTypography variant="body-01">{subtitle}</DSTypography> : null}
      <ProductBoxSliderFixedButtonsGalleryProducts
        products={products}
        isLoading={isLoading}
        onReachEnd={onReachEnd}
        subtitle={subtitle}
        withDownloadCTA={withDownloadCTA}
        rows={rowsToUse}
      />
    </div>
  )
}

export function ProductBoxSliderFixedButtonsTrendingNow({
  title,
  subtitle,
  withDownloadCTA,
}: {
  title: string
  subtitle?: string
  withDownloadCTA?: boolean
}) {
  return (
    <div className="@lg:px-4 flex flex-col gap-4">
      <DSTypography variant="cf-title-compact-02" className="capitalize text-gray-100">
        {title}
      </DSTypography>
      {subtitle ? <DSTypography variant="body-01">{subtitle}</DSTypography> : null}
      <ProductBoxSliderFixedButtonsGallery
        subtitle={subtitle}
        withDownloadCTA={withDownloadCTA}
        rows={2}
      />
    </div>
  )
}

function ScrollToHelper({ id }: { id: string }) {
  return <div className="h-full w-0" id={`helper-${id}`} />
}

export function ProductBox({
  product,
  classNameContainer,
  useAbsoluteLink,
  withDownloadCTA,
}: {
  product: ProductCardProduct | ProductCardProductEnrolled
  classNameContainer?: string
  useAbsoluteLink?: boolean
  withDownloadCTA?: boolean
}) {
  const t = useTranslations('components.product_box')
  const isEnrolled = 'enrolled' in product

  let enrolledInfo = null

  if (isEnrolled) {
    enrolledInfo = (
      <div className="flex items-center justify-between text-12 text-cf-grey-1000 mb-2">
        <div className="flex items-center gap-1">
          <SchoolIconFill className="size-5 text-cf-grey-600" />
          {product.enrolled === 1
            ? t('one_student')
            : `${convertHighCount(product.enrolled)} ${t('students')}`}
        </div>
        <span>{product.duration}</span>
      </div>
    )
  }
  return (
    <div
      className={tw(
        '@lg:max-w-80 @md:max-w-72 relative max-w-64 shrink-0 px-2.5 py-1.5',
        classNameContainer,
      )}
    >
      <div className="flex flex-col rounded shadow-md">
        <div className="relative">
          <Link href={product.href}>
            <Image
              src={product.image}
              alt={product.name}
              width={300}
              height={200}
              loading="lazy"
              decoding="async"
              className="bg-gray-1000 aspect-[3/2] rounded-t object-contain"
            />
            {'level' in product && product.level ? <LevelBadge level={product.level} /> : null}
            {'source' in product && product.source === 'original' ? <SourceBadge /> : null}
          </Link>
          <HoverActions
            href={product.href}
            imageSrc={product.image}
            id={product.id}
            title={product.name}
            isLegacy={product.isLegacy}
          />
        </div>
        <div className="bg-cf-white flex flex-col gap-1 rounded-b p-2.5">
          {enrolledInfo}
          <div className="flex w-full items-center justify-between gap-1">
            <div className="min-w-0 flex-1">
              <Link href={product.href}>
                <p className="text-cf-sf-gray-100 hover:text-secondary-500 text-14 font-bold truncate">
                  {product.name}
                </p>
              </Link>
            </div>
            <div className="shrink-0">
              <ProductBoxCheck />
            </div>
          </div>
          <div>
            <ProductBoxCategory
              product={product}
              useAbsoluteLink={useAbsoluteLink}
              withDownloadCTA={withDownloadCTA}
              href={product.href}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function ProductBoxCategory({
  product,
  useAbsoluteLink,
  withDownloadCTA,
  href,
}: {
  product: ProductCardProduct
  useAbsoluteLink?: boolean
  withDownloadCTA?: boolean
  href: string
}) {
  const t = useTranslations('pages.search')
  return (
    <div className="flex truncate justify-between items-center">
      <p className="truncate">
        {t('in')}{' '}
        <Link
          href={useAbsoluteLink ? `${product.category?.slug}` : `/${product.category?.slug ?? ''}`}
          className="text-blue-500 hover:underline "
        >
          {capitaliseFirstLetter(product.category?.title ?? '')}
        </Link>
      </p>
      {withDownloadCTA ? (
        <Button variant="tertiary-grey" size="small" asChild>
          <Link href={href} className="flex items-center gap-1">
            <DownloadIcon />
            {t('download')}
          </Link>
        </Button>
      ) : null}
    </div>
  )
}
